<template>
  <ion-page>
    <ion-header class="header-container">
      <ion-toolbar class="header-toolbar">
        <ion-buttons slot="start">
          <ion-back-button
            default-href="/home"
            color="light"
            text=""
          ></ion-back-button>
        </ion-buttons>
        <ion-title class="page-title">Order History</ion-title>
      </ion-toolbar>
    </ion-header>
    <ion-content v-if="orders.length">
      <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
        <ion-refresher-content
          :pulling-icon="chevronDownCircleOutline"
          pulling-text="Pull to refresh"
          refreshing-spinner="circles"
          refreshing-text="Refreshing..."
        >
        </ion-refresher-content>
      </ion-refresher>
      <div v-for="order in orders" :key="order.id">
        <ion-row class="orderlist">
          <ion-col size="2">
            <img :src="order.cafe.shopImgUrl" class="order-icon" />
          </ion-col>
          <ion-col class="order-col2">
            <a
              :href="
                '/order-details?cafeId=' +
                order.cafe.cafeId +
                '&cartOrderIds=' +
                order.cartOrderIds
              "
              style="text-decoration: none"
            >
              <ion-icon class="ver-icon" src="assets/icon/tick-circle.svg" />
              <span
                :class="{
                  'order-delivered': order.status === 'collected',
                  'order-preparing':
                    order.status === 'received' ||
                    order.status === 'preparing' ||
                    order.status === 'order_placed',
                  'order-pending-payment': order.status === 'pending_payment',
                  'order-confirmed': order.status === 'confirmed',
                  'order-cancelled': order.status === 'cancelled',
                }"
              >
                {{
                  order.status === "order_placed" || order.status === "received"
                    ? "Received"
                    : order.status === "pending_payment"
                    ? "Awaiting Payment"
                    : (order.status?.charAt(0)?.toUpperCase() ?? "") +
                      (order.status?.slice(1) ?? "")
                }}
              </span>
              <ion-icon
                src="assets/icon/dot-separate.svg"
                class="dotsep-icon"
              />
              <span class="order-txtlight">{{ order.quantity }} item/s</span>
              <span class="order-txtbold">{{ order.cafe.shopName }}</span>
            </a>
          </ion-col>
          <ion-col class="order-col3">
            <div class="ion-float-right">
              <span class="order-txtlight">
                {{ formatOrderDate(order.dateTimeOrdered) }}
              </span>
              <span class="order-txtbold ion-text-right">
                $ {{ parseFloat(order.total).toFixed(2) }}
              </span>
            </div>
          </ion-col>
        </ion-row>
      </div>
    </ion-content>
    <ion-content v-else>
      <ion-refresher slot="fixed" @ionRefresh="handleRefresh($event)">
        <ion-refresher-content
          :pulling-icon="chevronDownCircleOutline"
          pulling-text="Pull to refresh"
          refreshing-spinner="circles"
          refreshing-text="Refreshing..."
        >
        </ion-refresher-content>
      </ion-refresher>
      <div style="padding: 1em; text-align: center">No result/s found.</div>
    </ion-content>
    <TheFooter name="TheFooter" />
  </ion-page>
</template>
<script>
import { defineComponent } from "vue";
import axios from "axios";
import TheFooter from "@/components/TheFooter.vue";
import { formatOrderDate } from "@/utils/dateFormat";
import {
  IonContent,
  IonHeader,
  IonButtons,
  IonIcon,
  IonToolbar,
  IonPage,
  IonRow,
  IonCol,
  IonAvatar,
  IonTitle,
  IonBackButton,
  IonRefresher,
  IonRefresherContent,
} from "@ionic/vue";
import { chevronDownCircleOutline } from "ionicons/icons";

const BASE_URI =
  process.env.VUE_APP_BASE_URI !== undefined
    ? process.env.VUE_APP_BASE_URI
    : "https://api.onecoffeeapp.com.au";

export default defineComponent({
  name: "OrderHistoryPage",
  components: {
    IonContent,
    IonHeader,
    IonButtons,
    IonIcon,
    IonToolbar,
    IonPage,
    IonRow,
    IonCol,
    IonAvatar,
    IonTitle,
    IonBackButton,
    IonRefresher,
    IonRefresherContent,
    TheFooter,
  },
  data() {
    return {
      orders: [],
      orderId: "",
      chevronDownCircleOutline,
    };
  },
  methods: {
    formatOrderDate,
    async handleRefresh(event) {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      const cafeId = urlParams.get("cafeId") || "";
      const userId = localStorage.getItem("id") || null;

      await this.getOrders(userId, cafeId);

      // Complete the refresh animation
      event.target.complete();
    },
    async getOrders(userId, cafeId) {
      let axiosConfig = {
        headers: {
          Authorization: localStorage.getItem("token"),
          "Content-Type": "application/json;charset=UTF-8",
        },
      };

      let url = userId
        ? BASE_URI +
          "/orders/get-orders/?userId=" +
          userId +
          "&cafeId=" +
          cafeId
        : BASE_URI + "/cafe/get-coffee-shops";

      let result = await axios
        .get(url, axiosConfig)
        .then((res) => res.data)
        .catch((err) => {
          throw err.response.status;
        });

      this.orders = result && result.data.length ? await result.data : [];
      if (this.orders.length > 0) {
        this.orderId = this.orders[0].uuid;
      }
    },
  },
  async created() {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const cafeId = urlParams.get("cafeId") || "";
    const userId = localStorage.getItem("id") || null;

    await this.getOrders(userId, cafeId);
  },
});
</script>
<style scoped>
.header-container {
  --background: #f87e63;
}

.header-toolbar {
  --background: #f87e63;
  --border-width: 0;
  --padding-top: 8px;
  --padding-bottom: 8px;
  --padding-start: 16px;
  --padding-end: 16px;
  --min-height: 64px;
}

.page-title {
  --color: #ffffff;
  font-family: "Gilroy", sans-serif;
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
}

.orderlist {
  border-bottom: 1px solid #f3f3f3;
  padding-top: 15px;
  padding-bottom: 10px;
}
.h1-ol {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 18px;
  line-height: 21px;
  color: #1b1b1b;
  display: block;
  margin-top: 15px;
  margin-bottom: 5px;
  margin-left: 5%;
}
.order-icon {
  width: 46px;
  height: 46px;
  margin-left: 10px;
  margin-right: 10px;
  object-fit: cover;
  border-radius: 8px;
}
.order-col2 {
  margin-left: 10px;
}
.order-col3 {
  margin-right: 10px;
}
.order-delivered {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 135%;
  text-align: right;
  color: #00bf60;
  text-transform: capitalize;
}
.order-preparing {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 135%;
  text-align: right;
  color: #f87e63;
  text-transform: capitalize;
}
.order-confirmed {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 135%;
  text-align: right;
  color: #ffa500;
  text-transform: capitalize;
}
.order-pending-payment {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 135%;
  text-align: right;
  color: #ff9800;
  text-transform: capitalize;
}
.order-cancelled {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 135%;
  text-align: right;
  color: #f04a3f;
  text-transform: capitalize;
}
.order-txtlight {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 135%;
  text-align: right;
  color: #828282;
}
.order-txtbold {
  font-family: "Gilroy";
  font-style: normal;
  font-weight: 600;
  font-size: 14px;
  line-height: 140%;
  color: #1b1b1b;
  display: block;
  padding-top: 5px;
}
.dotsep-icon {
  vertical-align: middle;
  width: 3px;
  height: 3px;
  padding-right: 5px;
  padding-left: 5px;
}
.ver-icon {
  vertical-align: middle;
  padding-right: 5px;
}
</style>
